import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import axios from 'axios';
import Navbar from './Navbar';
import '../styles/Payments.css';
import { SERVER_URL } from './../config';
import { AuthContext } from './../AuthContext';

const sanitizeSourceValue = (value) => {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : String(value);
};

const Payments = () => {
  const { token, isAuthenticated } = useContext(AuthContext);

  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  // Checkbox selection for totals recalculation
  const [selectedPaymentIds, setSelectedPaymentIds] = useState(() => new Set());
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [editablePayment, setEditablePayment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');

  const [showPaid, setShowPaid] = useState(true);
  const [showUnpaid, setShowUnpaid] = useState(true);
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [filterFromSource, setFilterFromSource] = useState('');
  const [filterDescription, setFilterDescription] = useState('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [accountsOptions, setAccountsOptions] = useState([]);

  const fetchPayments = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    try {
      const response = await axios.get(`${SERVER_URL}/payments/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(response.data)) {
        const normalized = response.data.map((payment) => ({
          ...payment,
          from_source: sanitizeSourceValue(payment.from_source),
        }));
        setPayments(normalized);
        // Default selection: include all payments in totals.
        setSelectedPaymentIds(new Set(normalized.map((p) => p.id)));
      } else {
        console.error('Unexpected response:', response.data);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  }, [token, isAuthenticated]);

  const fetchAccountOptions = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    try {
      const resp = await axios.get(`${SERVER_URL}/accounts/options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(resp.data)) setAccountsOptions(resp.data);
      else console.error('Unexpected accounts/options format:', resp.data);
    } catch (e) {
      console.error('Failed to load account options', e);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPayments();
      fetchAccountOptions();
    }
  }, [isAuthenticated, fetchPayments, fetchAccountOptions]);

  const applyFilters = useCallback(() => {
    const filtered = payments.filter((payment) => {
      const paidMatch = (showPaid && payment.paid) || (showUnpaid && !payment.paid);
      const activeMatch = (showActive && payment.active) || (showInactive && !payment.active);
      const normalizedSource = sanitizeSourceValue(payment.from_source);
      const normalizedDescription = sanitizeSourceValue(payment.description);
      const fromSourceMatch =
        filterFromSource === '' ||
        (normalizedSource &&
          normalizedSource.toLowerCase().includes(filterFromSource.toLowerCase()));
      const descriptionMatch =
        filterDescription === '' ||
        (normalizedDescription &&
          normalizedDescription.toLowerCase().includes(filterDescription.toLowerCase()));
      const pendingMatch = !pendingOnly || payment.pending;
      return paidMatch && activeMatch && fromSourceMatch && descriptionMatch && pendingMatch;
    });
    setFilteredPayments(filtered);
  }, [payments, showPaid, showUnpaid, showActive, showInactive, pendingOnly, filterFromSource, filterDescription]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // State convention: pending = the vendor accepted it but the bank has not posted it yet;
  // paid = it already shows at the bank (even if the bank itself labels it pending).
  // The two are mutually exclusive -> marking paid clears pending, marking pending clears paid.
  const handleTogglePaid = async (payment) => {
    const newPaid = !payment.paid;
    const patch = newPaid ? { paid: true, pending: false } : { paid: false };
    try {
      await axios.patch(
        `${SERVER_URL}/payments/${payment.id}/`,
        patch,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setPayments((prev) =>
        prev.map((p) => (p.id === payment.id ? { ...p, ...patch } : p))
      );
    } catch (error) {
      console.error('Error toggling paid:', error);
    }
  };

  const handleToggleActive = async (payment) => {
    const newActive = !payment.active;
    try {
      await axios.patch(
        `${SERVER_URL}/payments/${payment.id}/`,
        { active: newActive },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setPayments((prev) =>
        prev.map((p) => (p.id === payment.id ? { ...p, active: newActive } : p))
      );
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const handleTogglePending = async (payment) => {
    const newPending = !payment.pending;
    const patch = newPending ? { pending: true, paid: false } : { pending: false };
    try {
      await axios.patch(
        `${SERVER_URL}/payments/${payment.id}/`,
        patch,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setPayments((prev) =>
        prev.map((p) => (p.id === payment.id ? { ...p, ...patch } : p))
      );
    } catch (error) {
      console.error('Error toggling pending:', error);
    }
  };

  const openModal = (payment) => {
    setSelectedPayment(payment);
    setEditablePayment({ ...payment });
    setUpdateMessage('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedPayment(null);
    setEditablePayment(null);
    setUpdateMessage('');
    setIsModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? e.target.checked : value;
    setEditablePayment((prev) => ({
      ...prev,
      [name]: name === 'from_source' ? sanitizeSourceValue(newValue) : newValue,
    }));
  };

  const handleUpdate = async () => {
    if (!selectedPayment) return;
    try {
      const response = await axios.patch(
        `${SERVER_URL}/payments/${selectedPayment.id}/`,
        editablePayment,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      const updatedPayment = {
        ...response.data,
        from_source: sanitizeSourceValue(response.data?.from_source),
      };
      setPayments((prev) =>
        prev.map((p) => (p.id === selectedPayment.id ? updatedPayment : p))
      );
      setUpdateMessage('Payment updated successfully.');
    } catch (error) {
      console.error('Error updating payment:', error);
      setUpdateMessage('Failed to update payment. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!selectedPayment) return;
    try {
      await axios.delete(`${SERVER_URL}/payments/${selectedPayment.id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPayments((prev) => prev.filter((p) => p.id !== selectedPayment.id));
      closeModal();
    } catch (error) {
      console.error('Error deleting payment:', error);
    }
  };

  const selectedFilteredPayments = useMemo(() => {
    return filteredPayments.filter((p) => selectedPaymentIds.has(p.id));
  }, [filteredPayments, selectedPaymentIds]);

  const totalAmount = useMemo(() => {
    return selectedFilteredPayments.reduce(
      (sum, p) => sum + (!p.paid ? parseFloat(p.amount) : 0),
      0
    );
  }, [selectedFilteredPayments]);

  const sourceBreakdown = useMemo(() => {
    const breakdown = {};

    selectedFilteredPayments.forEach((payment) => {
      const source = sanitizeSourceValue(payment.from_source) || 'N/A';
      
      if (!breakdown[source]) {
        breakdown[source] = {
          total: 0,
          paid: 0,
          processing: 0
        };
      }
      
      const amount = parseFloat(payment.amount) || 0;
      breakdown[source].total += amount;
      
      if (payment.paid) {
        breakdown[source].paid += amount;
      } else {
        breakdown[source].processing += amount;
      }
    });
    
    return breakdown;
  }, [selectedFilteredPayments]);

  const toggleSelected = (paymentId) => {
    setSelectedPaymentIds((prev) => {
      const next = new Set(prev);
      if (next.has(paymentId)) next.delete(paymentId);
      else next.add(paymentId);
      return next;
    });
  };

  const allFilteredSelected = useMemo(() => {
    if (filteredPayments.length === 0) return false;
    return filteredPayments.every((p) => selectedPaymentIds.has(p.id));
  }, [filteredPayments, selectedPaymentIds]);

  const toggleSelectAllFiltered = () => {
    setSelectedPaymentIds((prev) => {
      const next = new Set(prev);
      const shouldSelectAll = !filteredPayments.every((p) => next.has(p.id));
      if (shouldSelectAll) {
        filteredPayments.forEach((p) => next.add(p.id));
      } else {
        filteredPayments.forEach((p) => next.delete(p.id));
      }
      return next;
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Intl.DateTimeFormat('en-US').format(new Date(year, month - 1, day));
  };

  const isDueToday = (dueDateString) => {
    if (!dueDateString) return false;
    const [year, month, day] = dueDateString.split('-').map(Number);
    const dueDate = new Date(year, month - 1, day);
    const today = new Date();
    return (
      dueDate.getFullYear() === today.getFullYear() &&
      dueDate.getMonth() === today.getMonth() &&
      dueDate.getDate() === today.getDate()
    );
  };

  return (
    <div className="payments">
      <Navbar />
      <div className="content">
        <h1>Payments</h1>
        <div className="top-buttons">
          <button className="filter-button" onClick={() => setIsFilterModalOpen(true)}>
            Filters
          </button>
        </div>

        {isFilterModalOpen && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={() => setIsFilterModalOpen(false)}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Filters</h2>
              <form>
                <label>
                  <input type="checkbox" checked={showPaid} onChange={(e) => setShowPaid(e.target.checked)} />
                  Show Paid
                </label>
                <label>
                  <input type="checkbox" checked={showUnpaid} onChange={(e) => setShowUnpaid(e.target.checked)} />
                  Show Unpaid
                </label>
                <label>
                  <input type="checkbox" checked={showActive} onChange={(e) => setShowActive(e.target.checked)} />
                  Show Active
                </label>
                <label>
                  <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
                  Show Inactive
                </label>
                <label>
                  <input type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} />
                  Pending only (awaiting bank posting)
                </label>
                <label>
                  From Source:
                  <input
                    type="text"
                    value={filterFromSource}
                    onChange={(e) => setFilterFromSource(e.target.value)}
                    placeholder="Enter source"
                  />
                </label>
                <label>
                  Description:
                  <input
                    type="text"
                    value={filterDescription}
                    onChange={(e) => setFilterDescription(e.target.value)}
                    placeholder="Enter description"
                  />
                </label>
                <div className="modal-buttons">
                  <button type="button" className="modal-button close-button" onClick={() => setIsFilterModalOpen(false)}>
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="payments-container">
          <table className="payments-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleSelectAllFiltered();
                    }}
                    title="Include/exclude all filtered payments in totals"
                  />
                </th>
                <th>Description</th>
                <th>Amount</th>
                <th>Due Date</th>
                <th>From Source</th>
                <th>Paid</th>
                <th>Pending</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment, index) => (
                <tr
                  key={`${payment.id}-${index}`}
                  className={`${index % 2 === 0 ? 'even' : 'odd'} ${isDueToday(payment.due_date) ? 'due-today' : ''}`}
                  onClick={(e) => {
                    if (e.target.type !== 'checkbox') openModal(payment);
                  }}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedPaymentIds.has(payment.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleSelected(payment.id);
                      }}
                      title="Include this payment in totals"
                    />
                  </td>
                  <td>{payment.description}</td>
                  <td>{payment.amount}</td>
                  <td>{payment.due_date ? formatDate(payment.due_date) : 'No Due Date'}</td>
                  <td>{sanitizeSourceValue(payment.from_source) || 'N/A'}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={payment.paid}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleTogglePaid(payment);
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      className="pending-checkbox"
                      checked={!!payment.pending}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleTogglePending(payment);
                      }}
                      title="Pending: payment recorded, awaiting bank posting"
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={payment.active}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggleActive(payment);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td><strong>Total</strong></td>
                <td><strong>{totalAmount.toFixed(2)}</strong></td>
                <td>
                  <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '6px' }}>
                    Included in totals: {selectedFilteredPayments.length} / {filteredPayments.length}
                  </div>
                  <div style={{ textAlign: 'left', fontSize: '0.9em' }}>
                    {Object.keys(sourceBreakdown).length > 0 ? (
                      Object.entries(sourceBreakdown).map(([source, amounts]) => (
                        <div key={source} style={{ marginBottom: '4px' }}>
                          <strong>{source}:</strong>
                          <div style={{ paddingLeft: '10px', fontSize: '0.85em' }}>
                            Total: ${amounts.total.toFixed(2)}<br />
                            Paid: ${amounts.paid.toFixed(2)}<br />
                            Processing: ${amounts.processing.toFixed(2)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <span>No data</span>
                    )}
                  </div>
                </td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>

          <div className="payments-tiles">
            <div className="payment-tile total-tile">
              <div className="payment-description"><strong>Total Amount</strong></div>
              <div className="payment-amount"><strong>{totalAmount.toFixed(2)}</strong></div>
            </div>
            {filteredPayments.map((payment, index) => (
              <div
                key={`${payment.id}-${index}`}
                className={`payment-tile ${isDueToday(payment.due_date) ? 'due-today' : ''}`}
                onClick={() => openModal(payment)}
              >
                <div className="payment-description">{payment.description}</div>
                <div className="payment-amount"><label>Amount: </label>{payment.amount}</div>
                <div className="payment-due-day"><label>Due Date: </label>{payment.due_date ? formatDate(payment.due_date) : 'No Due Date'}</div>
                <div className="payment-from-source"><label>From Source: </label>{sanitizeSourceValue(payment.from_source) || 'N/A'}</div>
                <div className="payment-status">
                  <label>Paid: </label>
                  <input
                    type="checkbox"
                    checked={payment.paid}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleTogglePaid(payment)}
                  />
                </div>
                <div className="payment-status">
                  <label>Active: </label>
                  <input
                    type="checkbox"
                    checked={payment.active}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleToggleActive(payment)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {isModalOpen && editablePayment && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={closeModal}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Edit Payment</h2>
              <form>
                <label>
                  Description:
                  <input
                    type="text"
                    name="description"
                    value={editablePayment.description}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Amount:
                  <input
                    type="number"
                    name="amount"
                    value={editablePayment.amount}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Due Date:
                  <input
                    type="date"
                    name="due_date"
                    value={editablePayment.due_date}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  From Source:
                  <select
                    name="from_source"
                    value={editablePayment.from_source || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Source</option>
                    {accountsOptions.map((opt) => {
                      const optionValue = sanitizeSourceValue(
                        opt.value ?? opt.label ?? ''
                      );
                      const optionLabel = sanitizeSourceValue(
                        opt.label ?? opt.value ?? ''
                      );
                      return (
                        <option key={opt.id} value={optionValue}>
                          {optionLabel}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label className="inline-checkbox">
                  Paid:
                  <input
                    type="checkbox"
                    name="paid"
                    checked={editablePayment.paid}
                    onChange={handleInputChange}
                  />
                </label>
                <label className="inline-checkbox">
                  Active:
                  <input
                    type="checkbox"
                    name="active"
                    checked={editablePayment.active}
                    onChange={handleInputChange}
                  />
                </label>
              </form>
              {updateMessage && (
                <div className={`modal-message ${updateMessage.includes('success') ? 'success' : 'error'}`}>
                  {updateMessage}
                </div>
              )}
              <div className="modal-buttons">
                <button className="modal-button" onClick={handleUpdate}>Update</button>
                <button className="modal-button delete-button" onClick={handleDelete}>Delete</button>
                <button className="modal-button close-button" onClick={closeModal}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payments;
